import React from 'react'
import axios from 'axios'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react'
import { describeApiError } from '@common/utils/apiErrors'
import {
  NotifyIconKey,
  NotifyType,
  resolveNotification,
} from '@common/utils/notificationStyle'

interface NotifyOptions {
  title?: string
  // Mantine ignores `show` while a notification with this id is on screen or queued, so a
  // repeated failure shows once — the same dedupe react-toastify's `toastId` gave.
  id?: string
  autoClose?: number | false
}

const ICONS: Record<NotifyIconKey, React.ReactNode> = {
  check: <IconCheck size={16} />,
  info: <IconInfoCircle size={16} />,
  warning: <IconAlertTriangle size={16} />,
  x: <IconX size={16} />,
}

const notify = (type: NotifyType, message: string, options: NotifyOptions = {}) => {
  const {
    color,
    iconKey,
    autoClose,
  } = resolveNotification(type, { autoClose: options.autoClose })

  notifications.show({
    id: options.id,
    title: options.title,
    message,
    color,
    icon: iconKey ? ICONS[iconKey] : undefined,
    autoClose,
  })
}

export const notifySuccess = (message: string, options?: NotifyOptions) =>
  notify('success', message, options)

export const notifyError = (message: string, options?: NotifyOptions) =>
  notify('error', message, options)

export const notifyInfo = (message: string, options?: NotifyOptions) =>
  notify('info', message, options)

// Replaces `displayToastError`, and keeps its contract: an error that is not from axios is
// logged rather than shown, and a failed request always shows something — the API message,
// else the fallback (with the status when the response carried no message).
export const notifyApiError = (
  error: unknown,
  fallback = 'Something went wrong',
  options?: NotifyOptions,
) => {
  if (!axios.isAxiosError(error)) {
    console.error(error)
    return
  }

  notifyError(describeApiError(error, fallback), options)
}
